import React, { useState } from "react";
import { useGetCombinedClaimsQuery } from "store/queries/claimCombinedClaims";

/**
 * CLE-2628 — the "Combined Claims" panel.
 *
 * Episode binding keys on the claim number, so two claims the payer treated as ONE encounter — an
 * office visit billed apart from the lab it drew blood for — were priced separately, and we kept
 * expecting a contracted rate for a line the payer had bundled into the other claim.
 *
 * Those claims are now priced as one case, and the claims list shows a single combined row. This
 * panel is what that row expands into: every claim in the encounter with its own numbers, so the gap
 * between "what we show" and "what the provider billed" stays inspectable.
 *
 * Renders nothing for an ungrouped claim (the overwhelming majority), so the modal is unchanged for
 * every claim that was never combined.
 */

const usd = (cents: number | null | undefined): string =>
  cents == null
    ? "-"
    : (cents / 100).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });

const cell: React.CSSProperties = {
  padding: "0.5rem 0.75rem",
  borderBottom: "1px solid #eee",
  fontSize: "13px",
  whiteSpace: "nowrap",
};

const headerCell: React.CSSProperties = {
  ...cell,
  fontWeight: 600,
  color: "#555",
  borderBottom: "1px solid #e0e0e0",
};

interface ICombinedClaimsSectionProps {
  claimId: string;
}

function CombinedClaimsSection({ claimId }: ICombinedClaimsSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { data, isLoading } = useGetCombinedClaimsQuery(claimId, {
    skip: !claimId,
  });

  const claims = data?.data ?? [];
  // The endpoint returns [] for an ungrouped claim, and a "group" of one is not a group.
  if (isLoading || claims.length < 2) return null;

  return (
    <div style={{ marginTop: "1.5rem" }}>
      <h3
        style={{
          marginTop: 0,
          marginBottom: "0.75rem",
          fontSize: "16px",
          fontWeight: 600,
        }}
      >
        Combined Claims ({claims.length})
      </h3>
      <div
        style={{
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          background: "#fafafa",
        }}
      >
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          style={{
            width: "100%",
            background: "transparent",
            border: "none",
            padding: "0.75rem 1rem",
            textAlign: "left",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
          }}
        >
          <span style={{ fontSize: "13px", color: "#333" }}>
            {/* Ani asked for the claim numbers on the combined row — this is where they live. */}
            {claims.map((c) => c.claimNumber || "-").join(", ")}
          </span>
          <span style={{ fontSize: "12px", color: "#666" }}>
            {isOpen ? "Hide" : "Show"} breakdown
          </span>
        </button>

        {isOpen && (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                background: "#fff",
              }}
            >
              <thead>
                <tr>
                  <th style={{ ...headerCell, textAlign: "left" }}>Claim #</th>
                  <th style={{ ...headerCell, textAlign: "left" }}>CPT</th>
                  <th style={{ ...headerCell, textAlign: "right" }}>Billed</th>
                  <th style={{ ...headerCell, textAlign: "right" }}>Allowed</th>
                  <th style={{ ...headerCell, textAlign: "right" }}>Paid</th>
                </tr>
              </thead>
              <tbody>
                {claims.map((claim) => (
                  <tr key={claim.id}>
                    <td style={{ ...cell, textAlign: "left" }}>
                      {claim.claimNumber || "-"}
                      {claim.isPrimary && (
                        <span
                          style={{
                            marginLeft: "0.5rem",
                            fontSize: "11px",
                            color: "#666",
                          }}
                        >
                          {/* The row that carries the combined money, so a reader can tell which
                              claim the totals above are attached to. */}
                          (combined total)
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        ...cell,
                        textAlign: "left",
                        whiteSpace: "normal",
                      }}
                    >
                      {claim.caseSignature || "-"}
                    </td>
                    <td style={{ ...cell, textAlign: "right" }}>
                      {usd(claim.chargeAmount)}
                    </td>
                    <td style={{ ...cell, textAlign: "right" }}>
                      {usd(claim.claimAmount)}
                    </td>
                    <td style={{ ...cell, textAlign: "right" }}>
                      {usd(claim.totalPaid)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default CombinedClaimsSection;
